/* ──────────────────────────────────────────────────────────────
 *  geometryGeneration — shared helper to fetch mesh geometry for
 *  a sample with progress reporting into useUIStore. Used by:
 *    - useComputeExecution (initial compute → best sample)
 *    - useSampleSelection (switching sample from search bar,
 *      graph click, or Explore Designs dialog)
 *
 *  CHANGES:
 *   - Per-view fetches now report SIMULATED progress while the
 *     /mesh request is in flight, not just before/after it. With
 *     only 1–2 views per bushing type, the old "update once before
 *     the await" approach meant the overlay sat at a fixed low %
 *     for the entire (sometimes multi-second) fetch, then jumped
 *     straight to 100% — no visible motion during the actual wait.
 *     Now progress eases toward a cap short of the view's target
 *     while waiting, and snaps to the real target once it resolves.
 * ────────────────────────────────────────────────────────────── */

import type { QueryClient } from '@tanstack/react-query';
import { getGeometry } from '@/api/geometryApi';
import { geometryKeys } from '@/hooks/useGeometry';
import { useJobStore } from '@/stores/useJobStore';
import { useUIStore } from '@/stores/useUIStore';
import { FLIP_CONFIGS } from '@/types/geometry';
import type { GeometryView } from '@/types/geometry';
import type { BushingType } from '@/types/bushing';

export interface GeometryGenParams {
  queryClient: QueryClient;
  jobId: string;
  sampleId: string;
  bushingType: BushingType;
  rawSampleData: Record<string, number>;
  coreMaterial: string;
  outerMaterial: string;
  interSleeveMaterial: string;
  coreId: number | null;
  effectiveMinArea: number;
  weightPockets: boolean;
  sleeveThickness: number;
  linearTravelRadialPlus: number | null;
  linearTravelRadialMinus: number | null;
  /** Progress range this call should report into. Defaults to 0–100. */
  progressStart?: number;
  progressEnd?: number;
}

/** Build the sample data payload sent to /mesh, injecting D1 travA/travB. */
export function buildSampleDataForGeometry(
  bushingType: BushingType,
  rawSampleData: Record<string, number>,
  linearTravelRadialPlus: number | null,
  linearTravelRadialMinus: number | null,
): Record<string, number> {
  if (bushingType !== 'D1') return rawSampleData;

  const diameterSwaging = Number(
    rawSampleData['DIAMETER_SWAGING'] ?? rawSampleData['diameter_swaging'] ?? 0,
  );

  const ltPlus = linearTravelRadialPlus ?? 2.0;
  const ltMinus = linearTravelRadialMinus ?? 2.0;

  return {
    ...rawSampleData,
    travA: ltPlus + diameterSwaging / 2,
    travB: ltMinus + diameterSwaging / 2,
  };
}

const VIEW_LABELS: Record<GeometryView, string> = {
  iso: 'Generating 3-D preview…',
  sxh: 'Creating cross-section…',
  axh: 'Creating axial cross-section…',
};

/** Sub-stage messages shown while a single view's request is in flight. */
const VIEW_WAIT_LABELS: Record<GeometryView, string[]> = {
  iso: ['Generating 3-D preview…', 'Meshing surfaces…', 'Rendering preview…'],
  sxh: ['Creating cross-section…', 'Computing section geometry…', 'Rendering cross-section…'],
  axh: [
    'Creating axial cross-section…',
    'Computing section geometry…',
    'Rendering cross-section…',
  ],
};

const SIM_TICK_MS = 220;
/** Fraction of the remaining gap to close on each tick — smaller = slower easing. */
const SIM_EASE_RATE = 0.1;
/** Simulated progress never crosses this fraction of the view's span while waiting. */
const SIM_CAP_FRACTION = 0.92;

function viewsForBushing(bushingType: BushingType): GeometryView[] {
  const flipConfig = FLIP_CONFIGS[bushingType] ?? { views: ['iso', 'sxh'] };
  return flipConfig.views as GeometryView[];
}

/**
 * True when every geometry view required for this bushing type is already
 * sitting in the react-query cache for (jobId, sampleId). Lets callers
 * skip the loading overlay entirely when switching back to a sample whose
 * geometry was already generated earlier in this compute job.
 */
export function isGeometryFullyCached(
  params: Omit<GeometryGenParams, 'progressStart' | 'progressEnd'>,
): boolean {
  const {
    queryClient,
    jobId,
    sampleId,
    bushingType,
    effectiveMinArea,
    coreMaterial,
    coreId,
    sleeveThickness,
  } = params;

  return viewsForBushing(bushingType).every((view) => {
    const queryKey = geometryKeys.mesh(
      jobId,
      sampleId,
      bushingType,
      view,
      effectiveMinArea,
      coreMaterial,
      coreId,
      sleeveThickness,
    );
    return queryClient.getQueryData(queryKey) !== undefined;
  });
}

/**
 * Runs `task`, driving useUIStore progress smoothly from `rangeStart` up
 * toward (but never reaching) `rangeEnd` while it's pending — ticking on
 * an interval and cycling through `waitLabels` — then snaps progress to
 * `rangeEnd` once `task` settles (success or failure).
 */
async function runWithSimulatedProgress<T>(
  task: () => Promise<T>,
  rangeStart: number,
  rangeEnd: number,
  waitLabels: string[],
): Promise<T> {
  const span = rangeEnd - rangeStart;
  const cap = rangeStart + span * SIM_CAP_FRACTION;

  let current = rangeStart;
  let labelIdx = 0;

  useUIStore.getState().updateProgress(Math.round(current), waitLabels[0]);

  const timer = setInterval(() => {
    const remaining = cap - current;
    current += remaining * SIM_EASE_RATE;
    if (current > cap) current = cap;

    // Advance to the next sub-stage label partway through the climb so
    // the message changes even when there's only one view.
    const nextLabelIdx = current > rangeStart + span * 0.35 ? 1 : 0;
    const finalLabelIdx =
      current > rangeStart + span * 0.7 ? waitLabels.length - 1 : nextLabelIdx;
    labelIdx = Math.min(finalLabelIdx, waitLabels.length - 1);

    useUIStore.getState().updateProgress(Math.round(current), waitLabels[labelIdx]);
  }, SIM_TICK_MS);

  try {
    const result = await task();
    return result;
  } finally {
    clearInterval(timer);
  }
}

/**
 * Fetch (or reuse from cache) every geometry view required for the given
 * bushing type, reporting progress through useUIStore.updateProgress
 * (including simulated in-flight progress per view — see
 * runWithSimulatedProgress), and marking useJobStore.geometryReady for
 * (jobId, sampleId) as soon as any view succeeds. Returns true if at
 * least one view succeeded.
 *
 * Caller is responsible for showLoading()/hideLoading() around this call.
 */
export async function generateGeometryWithProgress(
  params: GeometryGenParams,
): Promise<boolean> {
  const {
    queryClient,
    jobId,
    sampleId,
    bushingType,
    rawSampleData,
    coreMaterial,
    outerMaterial,
    interSleeveMaterial,
    coreId,
    effectiveMinArea,
    weightPockets,
    sleeveThickness,
    linearTravelRadialPlus,
    linearTravelRadialMinus,
    progressStart = 0,
    progressEnd = 100,
  } = params;

  const sampleData = buildSampleDataForGeometry(
    bushingType,
    rawSampleData,
    linearTravelRadialPlus,
    linearTravelRadialMinus,
  );

  const views = viewsForBushing(bushingType);
  const span = progressEnd - progressStart;
  const progressPerView = views.length > 0 ? span / views.length : span;

  let succeeded = false;

  for (let i = 0; i < views.length; i++) {
    const view = views[i];
    const viewStart = progressStart + i * progressPerView;
    const viewEnd = viewStart + progressPerView;

    const queryKey = geometryKeys.mesh(
      jobId,
      sampleId,
      bushingType,
      view,
      effectiveMinArea,
      coreMaterial,
      coreId,
      sleeveThickness,
    );

    try {
      await runWithSimulatedProgress(
        () =>
          queryClient.fetchQuery({
            queryKey,
            queryFn: () =>
              getGeometry({
                jobId,
                sampleId,
                bushingType,
                viewType: view,
                sampleData,
                coreMaterial,
                outerMaterial,
                interSleeveMaterial,
                coreId,
                minArea: effectiveMinArea,
                weightPockets,
                sleeveThickness,
              }),
            staleTime: 30 * 60 * 1000,
          }),
        viewStart,
        viewEnd,
        VIEW_WAIT_LABELS[view] ?? [VIEW_LABELS[view]],
      );

      succeeded = true;
      useJobStore.getState().markGeometryReady(jobId, sampleId);
      useUIStore.getState().updateProgress(Math.round(viewEnd));
    } catch {
      // Best-effort; the reactive useGeometry hook will retry on render.
      // Still advance progress to viewEnd so a failed view doesn't stall
      // the bar for the rest of the sequence.
      useUIStore.getState().updateProgress(Math.round(viewEnd));
    }
  }

  useUIStore.getState().updateProgress(progressEnd, 'Preview ready');

  return succeeded;
}